import { useQuery } from '@tanstack/react-query';
import { CircleAlert, LoaderCircle } from 'lucide-react';
import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';

import { AnalyticsIdentity } from '@/_shared/components/AnalyticsIdentity';
import { AnalyticsPageView } from '@/_shared/components/AnalyticsPageView';
import { AppLayout } from '@/_shared/components/AppLayout';
import {
  GatewayActions,
  GatewayBrand,
  GatewayContent,
  GatewayShell,
  GatewaySpinner,
} from '@/_shared/components/GatewayShell';
import { LoginRedirect } from '@/_shared/components/LoginRedirect';
import { RadixBodyLockScrub } from '@/_shared/components/RadixBodyLockScrub';
import { Button } from '@/_shared/components/ui/button';
import { Toaster } from '@/_shared/components/ui/sonner';
import { TooltipProvider } from '@/_shared/components/ui/tooltip';
import { WorkspaceRoute } from '@/_shared/components/WorkspaceRoute';
import { useAuth } from '@/_shared/hooks/useAuth';
import { clearLastWorkspaceId, getLastWorkspaceId } from '@/_shared/lib/last-workspace';
import { orpc } from '@/_shared/lib/orpc';
import { isWorkspaceChatPath } from '@/_shared/lib/workspace-chat-path';

import { ActivationDetail } from './activations/views/ActivationDetail';
import { ActivationsList } from './activations/views/ActivationsList';
import { RequireAudiences } from './audiences/components/RequireAudiences';
import { AudienceList } from './audiences/views/AudienceList';
import { AudiencePage } from './audiences/views/AudiencePage';
import { SharedSessionKeeper } from './auth/components/SharedSessionKeeper';
import { webAppEntrySignInUrl } from './auth/lib/web-app-entry';
import { AcceptInvite } from './auth/views/AcceptInvite';
import { ForgotPassword } from './auth/views/ForgotPassword';
import { HostedUiCallback } from './auth/views/HostedUiCallback';
import { Login } from './auth/views/Login';
import { NewWorkspacePicker } from './auth/views/NewWorkspacePicker';
import { ResetPassword } from './auth/views/ResetPassword';
import { RudderstackInvite } from './auth/views/RudderstackInvite';
import { RudderstackOauthLanding } from './auth/views/RudderstackOauthLanding';
import { Signup } from './auth/views/Signup';
import { SignupComplete } from './auth/views/SignupComplete';
import { WorkspaceSelect } from './auth/views/WorkspaceSelect';
import { Chat } from './chat/views/Chat';
import { ChatList } from './chat/views/ChatList';
import { Dashboard } from './dashboard/views/Dashboard';
import { DashboardList } from './dashboard/views/DashboardList';
import { PublicDashboard } from './dashboard/views/PublicDashboard';
import { RequireRsAccounts } from './settings/components/RequireRsAccounts';
import { RequireUsage } from './settings/components/RequireUsage';
import { SettingsShell } from './settings/components/SettingsShell';
import { Settings } from './settings/views/Settings';
import { SettingsAccounts } from './settings/views/SettingsAccounts';
import { SettingsContext } from './settings/views/SettingsContext';
import { SettingsIntegrations } from './settings/views/SettingsIntegrations';
import { SettingsUsage } from './settings/views/SettingsUsage';
import { SettingsUsers } from './settings/views/SettingsUsers';
import { SettingsWarehouses } from './settings/views/SettingsWarehouses';
import { RequireWorkflowsV2 } from './workflow/components/RequireWorkflowsV2';
import { WorkflowDetail } from './workflow/views/WorkflowDetail';
import { Workflows } from './workflow/views/Workflows';

const DocDetail = lazy(() => import('./context-hub/views/DocDetail.js'));
const ContextHub = lazy(() => import('./context-hub/views/ContextHub.js'));
const ContextReview = lazy(() => import('./context-hub/views/ContextReview.js'));
const ContextItemDetail = lazy(() =>
  import('./context-hub/views/ContextItemDetail.js').then((m) => ({ default: m.ContextItemDetail })),
);
const SkillDetail = lazy(() => import('./skills/views/Skills.js').then((m) => ({ default: m.SkillDetail })));

function FullScreenSpinner(): React.ReactElement {
  return <GatewaySpinner mode="fullScreen" />;
}

function ProtectedRoute({ children }: { children: React.ReactNode }): React.ReactElement {
  const { user, workspaces, loading, error, signOutAndRedirect } = useAuth();
  const location = useLocation();

  if (loading) {
    return <FullScreenSpinner />;
  }

  if (error) {
    const isLocal =
      typeof window !== 'undefined' &&
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

    return (
      <GatewayShell>
        <GatewayBrand />
        <GatewayContent
          icon={<CircleAlert />}
          variant="error"
          title="Unable to verify your session"
          description="Your session may have expired or the server is temporarily unavailable."
        />
        <GatewayActions>
          <Button variant="primary" size="lg" className="w-full" onClick={() => window.location.reload()}>
            Refresh page
          </Button>
          <Button variant="secondary" size="lg" className="w-full" onClick={() => void signOutAndRedirect()}>
            Sign out and return to login
          </Button>
          {isLocal && (
            <p className="text-xs text-muted-foreground">Dev mode: check your terminal for a fresh magic link.</p>
          )}
        </GatewayActions>
      </GatewayShell>
    );
  }

  if (!user) {
    return <LoginRedirect />;
  }

  // Chat links must reach WorkspaceRoute's explanation even when the user has no workspaces.
  // Other no-workspace visits keep the pending-invite / onboarding flow.
  if (workspaces.length === 0 && !isWorkspaceChatPath(location.pathname)) {
    const pendingInvite = sessionStorage.getItem('pending_invite');
    if (pendingInvite) {
      return <Navigate to={`/invite/${pendingInvite}`} replace />;
    }
    return <Navigate to="/new-workspace" replace />;
  }

  // Workspace membership validation is handled by WorkspaceRoute
  return <>{children}</>;
}

function LoginRoute(): React.ReactElement {
  const { user, loading, error } = useAuth();
  const location = useLocation();
  const { data: providers, isLoading: providersLoading } = useQuery(orpc.auth.providers.queryOptions());
  // A signed-out visit from the RudderStack web app's sidebar skips the sign-in card (ACT2-808).
  // `error` is checked alongside `user`: a failed `auth.me` request also reports `user: null`.
  // Starting sign-in on it would send someone with a valid session into a login they do not need.
  // A database outage is not covered: `auth.me` then answers `user: null` without an error.
  // An unverifiable session falls back to the card, which they can use deliberately.
  const sidebarSignInUrl = loading || user || error ? null : webAppEntrySignInUrl(location.search);
  // With Lookout's own sign-in (ACT2-871), the login page serves the visit, and it keeps the entry.
  // A failed providers probe keeps the redirect, as before the probe was here.
  const webAppEntryUrl = sidebarSignInUrl && !providersLoading && !providers?.nativeSignIn ? sidebarSignInUrl : null;

  useEffect(() => {
    // A full-page navigation: /auth/rudderstack is served by the Lookout backend, not this router.
    // `replace` keeps /login out of history, so Back does not land on it.
    if (webAppEntryUrl) window.location.replace(webAppEntryUrl);
  }, [webAppEntryUrl]);

  if (loading || webAppEntryUrl || (sidebarSignInUrl && providersLoading)) return <FullScreenSpinner />;
  // Logged-in users go to the home resolver, which auto-enters a single workspace,
  // shows the picker for 2+, or onboards when there are none.
  if (user) return <Navigate to="/" replace />;
  return <Login />;
}

/**
 * Home resolver for any URL without a workspace in the path (`/`, plus legacy bookmarks
 * like `/chats` or `/settings/warehouses`). Resolves the landing target while preserving the
 * intended deep path:
 *   - 0 workspaces       → /new-workspace onboarding
 *   - 1 workspace        → auto-enter it at /w/:id{intendedPath}
 *   - 2+ workspaces      → render the workspace picker
 * The intended path comes from router `state.from` (set by an invalid-slug redirect) when
 * present, otherwise from the current location.
 */
export function NoWorkspaceLanding(): React.ReactElement {
  const { user, workspaces, loading } = useAuth();
  const location = useLocation();

  if (loading) return <FullScreenSpinner />;

  if (!user) return <LoginRedirect />;

  // An explicit picker request wins over automatic entry, onboarding, and pending-invite redirects.
  // With no workspaces, the picker still offers invitations, creation, and switching accounts.
  const state = location.state as { from?: string; pickWorkspace?: boolean } | null;
  if (state?.pickWorkspace) return <WorkspaceSelect intendedPath={state.from ?? ''} />;

  // Check for pending invite before onboarding redirect
  const pendingInvite = sessionStorage.getItem('pending_invite');
  if (pendingInvite) {
    return <Navigate to={`/invite/${pendingInvite}`} replace />;
  }

  if (workspaces.length === 0) {
    return <Navigate to="/new-workspace" replace />;
  }

  // Preserve the full path + query the user was aiming for: /settings/context?github_success=true
  // → /w/:id/settings/context?github_success=true
  const intendedPath = state?.from ?? (location.pathname === '/' ? '' : `${location.pathname}${location.search}`);

  if (workspaces.length === 1) {
    return <Navigate to={`/w/${workspaces[0]!.id}${intendedPath}`} replace />;
  }

  // 2+ workspaces: re-enter the last-visited one if it's still a workspace the user belongs to.
  const lastId = getLastWorkspaceId();
  if (lastId) {
    if (workspaces.some((w) => w.id === lastId)) {
      return <Navigate to={`/w/${lastId}${intendedPath}`} replace />;
    }
    clearLastWorkspaceId();
  }

  return <WorkspaceSelect intendedPath={intendedPath} />;
}

/** Unknown sub-path under a *valid* workspace → that workspace's home, not the global picker. */
export function WorkspaceCatchAll(): React.ReactElement {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={`/w/${slug}`} replace />;
}

/**
 * The settings `*` route renders this. It replaces the old per-path redirects: a retired settings path
 * (such as `connections/scm` or `slack`) or an unknown one opens the General settings tab. The workspace
 * catch-all would send those paths to the workspace home instead.
 */
function SettingsCatchAll(): React.ReactElement {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={`/w/${slug}/settings`} replace />;
}

// Legacy `/docs/:id` is an alias for the canonical generated-doc route. Redirect
// so bookmarked URLs keep working and all path-based logic sees one canonical path.
export function LegacyDocRedirect(): React.ReactElement {
  const { slug, id } = useParams<{ slug: string; id: string }>();
  return <Navigate to={`/w/${slug}/context/generated/${id}`} replace />;
}

export function AppRoutes(): React.ReactElement {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginRoute />} />
      {/* Google and SSO with the shared session (ACT2-879): the Hosted UI returns here. */}
      <Route path="/login/callback" element={<HostedUiCallback />} />
      {/* Lookout's own sign-up and password pages (ACT2-871); each redirects home while the flag is off. */}
      <Route path="/signup" element={<Signup />} />
      <Route path="/signup/complete" element={<SignupComplete />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      {/* A RudderStack invitation from Lookout (ACT2-1124): works before sign-in. */}
      <Route path="/rudderstack-invite" element={<RudderstackInvite />} />
      {/* RudderStack account OAuth return — public, stateless; see RudderstackOauthLanding. */}
      <Route path="/oauth/rudderstack" element={<RudderstackOauthLanding />} />
      <Route path="/pub/:token" element={<PublicDashboard />} />
      <Route path="/invite/:inviteId" element={<AcceptInvite />} />
      <Route path="/new-workspace" element={<NewWorkspacePicker />} />

      {/* Workspace routes: /w/:slug/... */}
      <Route
        path="/w/:slug"
        element={
          <ProtectedRoute>
            <WorkspaceRoute />
          </ProtectedRoute>
        }
      >
        <Route element={<AppLayout />}>
          <Route index element={<Chat />} />
          <Route path="chat/:id" element={<Chat />} />
          <Route path="chats" element={<ChatList />} />
          <Route path="d/:dashSlug" element={<Dashboard />} />
          <Route path="dashboards" element={<DashboardList />} />
          <Route
            path="audiences"
            element={
              <RequireAudiences>
                <AudienceList />
              </RequireAudiences>
            }
          />
          <Route
            path="audiences/:id"
            element={
              <RequireAudiences>
                <AudiencePage />
              </RequireAudiences>
            }
          />
          {/* Activations ride the audiences gate — an activation is how an audience leaves the
              building, so a workspace that cannot build one has nothing to activate. The detail
              route carries :sourceId as well as :syncId because a sync is only reachable through
              its source; see ActivationDetail. */}
          <Route
            path="activations"
            element={
              <RequireAudiences>
                <ActivationsList />
              </RequireAudiences>
            }
          />
          <Route
            path="activations/:sourceId/:syncId"
            element={
              <RequireAudiences>
                <ActivationDetail />
              </RequireAudiences>
            }
          />
          <Route
            path="workflows"
            element={
              <RequireWorkflowsV2>
                <Workflows />
              </RequireWorkflowsV2>
            }
          />
          <Route
            path="workflows/new"
            element={
              <RequireWorkflowsV2>
                <WorkflowDetail />
              </RequireWorkflowsV2>
            }
          />
          <Route
            path="workflows/:id"
            element={
              <RequireWorkflowsV2>
                <WorkflowDetail />
              </RequireWorkflowsV2>
            }
          />
          <Route path="docs" element={<Navigate to="../context" replace />} />
          <Route path="docs/:id" element={<LegacyDocRedirect />} />
          <Route
            path="context"
            element={
              <Suspense fallback={<LoaderCircle className="mx-auto mt-12 size-6 animate-spin text-muted-foreground" />}>
                <ContextHub />
              </Suspense>
            }
          />
          <Route
            path="context/skill/:id"
            element={
              <Suspense fallback={<LoaderCircle className="mx-auto mt-12 size-6 animate-spin text-muted-foreground" />}>
                <SkillDetail />
              </Suspense>
            }
          />
          <Route
            path="context/review"
            element={
              <Suspense fallback={<LoaderCircle className="mx-auto mt-12 size-6 animate-spin text-muted-foreground" />}>
                <ContextReview />
              </Suspense>
            }
          />
          <Route
            path="context/generated/:id"
            element={
              <Suspense fallback={<LoaderCircle className="mx-auto mt-12 size-6 animate-spin text-muted-foreground" />}>
                <DocDetail />
              </Suspense>
            }
          />
          <Route
            path="context/:kind/:id"
            element={
              <Suspense fallback={<LoaderCircle className="mx-auto mt-12 size-6 animate-spin text-muted-foreground" />}>
                <ContextItemDetail />
              </Suspense>
            }
          />
          <Route path="settings" element={<SettingsShell />}>
            <Route index element={<Settings />} />
            <Route path="users" element={<SettingsUsers />} />
            <Route path="warehouses" element={<SettingsWarehouses />} />
            <Route path="context" element={<SettingsContext />} />
            <Route
              path="accounts"
              element={
                <RequireRsAccounts>
                  <SettingsAccounts />
                </RequireRsAccounts>
              }
            />
            {/* The bare path opens the default section. SettingsIntegrations sends an unknown section to
                General, as the catch-all below does. */}
            <Route path="integrations" element={<SettingsIntegrations />} />
            <Route path="integrations/:section" element={<SettingsIntegrations />} />
            <Route
              path="usage"
              element={
                <RequireUsage>
                  <SettingsUsage />
                </RequireUsage>
              }
            />
            {/* Any other settings path opens General. That includes the retired ones (`connections/*`,
                `scm`, `slack`, `members`, `permissions`, `pricing`, …), which no longer have their own redirects. */}
            <Route path="*" element={<SettingsCatchAll />} />
          </Route>
        </Route>
        {/* Unknown sub-path under a valid workspace → workspace home (not the global picker) */}
        <Route path="*" element={<WorkspaceCatchAll />} />
      </Route>

      {/* Home + legacy bookmarks: resolve a workspace (or show the picker), preserving the deep path */}
      <Route path="/" element={<NoWorkspaceLanding />} />
      <Route path="/chats" element={<NoWorkspaceLanding />} />
      <Route path="/chat/:id" element={<NoWorkspaceLanding />} />
      <Route path="/dashboards" element={<NoWorkspaceLanding />} />
      <Route path="/d/:slug" element={<NoWorkspaceLanding />} />
      <Route path="/workflows" element={<NoWorkspaceLanding />} />
      <Route path="/workflows/:id" element={<NoWorkspaceLanding />} />
      <Route path="/context/*" element={<NoWorkspaceLanding />} />
      <Route path="/settings/*" element={<NoWorkspaceLanding />} />

      {/* Anything else (invalid/unknown path) → home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export function App(): React.ReactElement {
  return (
    /* One app-wide TooltipProvider so no component declares its own. A custom delay goes on the Tooltip root. */
    <TooltipProvider>
      <BrowserRouter>
        <AnalyticsIdentity />
        <AnalyticsPageView />
        <SharedSessionKeeper />
        <RadixBodyLockScrub />
        {/* One app-wide Toaster so toasts render on every route — auth/picker views render outside
            AppLayout, and a per-route mount is easy to forget (AI-1030 fail-closed logout toast). */}
        <Toaster />
        <AppRoutes />
      </BrowserRouter>
    </TooltipProvider>
  );
}
